import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  RdsAccordionDirective,
  RdsButtonComponent,
  RdsExpansionPanelComponent,
  RdsExpansionPanelHeaderComponent,
  RdsExpansionPanelTitleDirective,
  RdsFormFieldComponent,
  RdsLabelDirective,
  RdsMultiSelectComponent,
  RdsMultiSelectOptionComponent,
  RdsProgressSpinnerComponent,
  RdsSearchComponent,
  RdsTabChangeEvent,
  RdsTabComponent,
  RdsTabsComponent,
  Sort,
} from '@rds/angular-components';
import { RdsPaginatorPageEvent } from '@rds/core';
import { debounceTime, distinctUntilChanged, switchMap, take } from 'rxjs';

import {
  BOOKING_REQUEST_SORT_FIELDS,
  BookingRequestSortField,
  BookingStatusApiResponseEnum,
  RoleType,
  RoomBookingModel,
  RoomServicesEnum,
  TablePaginationState,
  ViewportService,
} from '@bookly/shared';

import { RoomsService } from '../../../room-management/services/rooms.service';
import { RoomBookingsTableComponent } from '../../components/room-bookings-table/room-bookings-table.component';
import {
  BookingRequestFilters,
  BookingRequestService,
} from '../../services/booking-request.service';

@Component({
  selector: 'bad-booking-request',
  templateUrl: './booking-request.component.html',
  styleUrls: ['./booking-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RdsAccordionDirective,
    RdsExpansionPanelComponent,
    RdsExpansionPanelHeaderComponent,
    RdsExpansionPanelTitleDirective,
    RoomBookingsTableComponent,
    ReactiveFormsModule,
    RdsButtonComponent,
    RdsSearchComponent,
    RdsFormFieldComponent,
    RdsLabelDirective,
    RdsMultiSelectComponent,
    RdsMultiSelectOptionComponent,
    RdsTabsComponent,
    RdsTabComponent,
    RdsProgressSpinnerComponent,
  ],
})
export class BookingRequestComponent implements OnInit, OnDestroy {
  /**
   * Collapsed by default where the filters would otherwise push the content off
   * screen. Seeded once, so resizing does not fight the user's own toggling.
   */
  protected readonly filtersExpanded = signal(
    !inject(ViewportService).isCompact()
  );

  readonly #bookingRequestService = inject(BookingRequestService);
  readonly #roomsService = inject(RoomsService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #destroy$ = inject(DestroyRef);
  readonly #fb = inject(FormBuilder);

  protected readonly allBookings = this.#bookingRequestService.allBookings;
  protected readonly isRoomBookingDataLoading =
    this.#bookingRequestService.isRoomBookingDataLoading;
  protected readonly paginationState = computed<TablePaginationState>(() => ({
    length: this.#bookingRequestService.length(),
    pageIndex: this.#bookingRequestService.page(),
    pageSize: this.#bookingRequestService.pageSize(),
    sortBy: this.#bookingRequestService.sortBy(),
    sortDirection: this.#bookingRequestService.sortDirection(),
  }));
  protected readonly TAB_STATUS_MAP: (BookingStatusApiResponseEnum | null)[] = [
    BookingStatusApiResponseEnum.InProgress,
    BookingStatusApiResponseEnum.Approved,
    null,
  ];
  protected role: RoleType | null = null;
  protected services = this.#roomsService.roomServices;

  protected filterForm: FormGroup = this.#fb.group({
    searchPhrase: [''],
    services: [[]],
    siteNames: [[]],
    buildingNames: [[]],
    floorNames: [[]],
  });

  protected readonly siteNames = this.#bookingRequestService.siteNames;
  protected readonly buildingNames = this.#bookingRequestService.buildingNames;
  protected readonly floorNames = this.#bookingRequestService.floorNames;

  #appliedFiltersSnapshot = '';

  public ngOnInit() {
    this.role = this.#route.snapshot.data['role'] as RoleType | null;
    this.#bookingRequestService.setStatus(
      BookingStatusApiResponseEnum.InProgress
    );
    this.#roomsService
      .getRoomServices()
      .pipe(
        switchMap(services => {
          this.#initFiltersFromQueryParams();
          return this.#bookingRequestService.getRoomBookings(
            this.role,
            services
          );
        })
      )
      .subscribe(() => {
        this.#subscribeToFilterChanges();
      });
  }

  public ngOnDestroy() {
    this.#bookingRequestService.clearSearchQuery();
    this.#bookingRequestService.clearBookingRequestFilters();
  }

  protected clickRowHandler(data: RoomBookingModel) {
    void this.#router.navigate([data.roomDexId, data.id], {
      relativeTo: this.#route,
    });
  }

  protected handleTabChange(event: RdsTabChangeEvent) {
    const status = this.TAB_STATUS_MAP[event.index] ?? null;
    // rds-tabs emits selectedTabChange on initial render for the default tab,
    // which would duplicate the fetch already triggered in ngOnInit. Only
    // refetch when the status actually changes.
    if (status === this.#bookingRequestService.status()) {
      return;
    }
    this.#bookingRequestService.setStatus(status);
    this.#bookingRequestService
      .getRoomBookings(this.role)
      .pipe(take(1))
      .subscribe();
  }

  protected handlePageChange(event: RdsPaginatorPageEvent) {
    this.#bookingRequestService.setPage(event.pageIndex, event.pageSize);
    this.#bookingRequestService
      .getRoomBookings(this.role)
      .pipe(take(1))
      .subscribe();
  }

  protected handleSortChange(event: Sort) {
    const sortBy = BOOKING_REQUEST_SORT_FIELDS.includes(
      event.active as BookingRequestSortField
    )
      ? (event.active as BookingRequestSortField)
      : 'id';
    this.#bookingRequestService.setSort(sortBy, event.direction);
    this.#bookingRequestService
      .getRoomBookings(this.role)
      .pipe(take(1))
      .subscribe();
  }

  protected clearFilters() {
    this.filterForm.reset({
      searchPhrase: '',
      siteNames: [],
      buildingNames: [],
      floorNames: [],
      services: [],
    });
    this.#appliedFiltersSnapshot = JSON.stringify(this.filterForm.value);
    this.#bookingRequestService.clearBookingRequestFilters();
    this.#clearFilterQueryParams();
  }

  #paramToArray(value: string | null): string[] {
    return value ? value.split(',').filter(Boolean) : [];
  }

  #arrayToParam(value: string[] | undefined): string | null {
    return value?.length ? value.join(',') : null;
  }

  #initFiltersFromQueryParams() {
    const queryParams = this.#route.snapshot.queryParamMap;
    const filters: BookingRequestFilters = {
      searchPhrase: queryParams.get('searchPhrase') || null,
      services: this.#paramToArray(
        queryParams.get('services')
      ) as RoomServicesEnum[],
      siteNames: this.#paramToArray(queryParams.get('siteNames')),
      buildingNames: this.#paramToArray(queryParams.get('buildingNames')),
      floorNames: this.#paramToArray(queryParams.get('floorNames')),
    };

    const hasFilters =
      filters.searchPhrase ||
      (filters.services && filters.services.length > 0) ||
      (filters.siteNames && filters.siteNames.length > 0) ||
      (filters.buildingNames && filters.buildingNames.length > 0) ||
      (filters.floorNames && filters.floorNames.length > 0);

    if (hasFilters) {
      this.filterForm.patchValue(filters, { emitEvent: false });
      this.#bookingRequestService.setBookingRequestFilters(filters);
    }
    // Sync snapshot so opening/closing a dropdown without changes doesn't reload
    this.#appliedFiltersSnapshot = JSON.stringify(this.filterForm.value);
  }

  #updateUrlWithFilters() {
    const formValue = this.filterForm.value;
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        searchPhrase: formValue.searchPhrase || null,
        services: this.#arrayToParam(formValue.services),
        siteNames: this.#arrayToParam(formValue.siteNames),
        buildingNames: this.#arrayToParam(formValue.buildingNames),
        floorNames: this.#arrayToParam(formValue.floorNames),
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  #clearFilterQueryParams() {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        searchPhrase: null,
        services: null,
        siteNames: null,
        buildingNames: null,
        floorNames: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  #subscribeToFilterChanges() {
    this.filterForm
      .get('siteNames')!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroy$))
      .subscribe(() => {
        const currentBuildings = this.buildingNames();
        const selectedBuildings: string[] =
          this.filterForm.get('buildingNames')!.value || [];
        const validBuildings = selectedBuildings.filter(b =>
          currentBuildings.includes(b)
        );
        if (validBuildings.length !== selectedBuildings.length) {
          this.filterForm
            .get('buildingNames')!
            .setValue(validBuildings, { emitEvent: true });
        }
      });

    this.filterForm
      .get('buildingNames')!
      .valueChanges.pipe(takeUntilDestroyed(this.#destroy$))
      .subscribe(() => {
        const currentFloors = this.floorNames();
        const selectedFloors: string[] =
          this.filterForm.get('floorNames')!.value || [];
        const validFloors = selectedFloors.filter(f =>
          currentFloors.includes(f)
        );
        if (validFloors.length !== selectedFloors.length) {
          this.filterForm
            .get('floorNames')!
            .setValue(validFloors, { emitEvent: true });
        }
      });

    this.filterForm
      .get('searchPhrase')!
      .valueChanges.pipe(
        takeUntilDestroyed(this.#destroy$),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(() => {
          const values = this.filterForm.value;
          this.#appliedFiltersSnapshot = JSON.stringify(values);
          this.#bookingRequestService.setBookingRequestFilters(values);
          this.#bookingRequestService.resetPage();
          this.#updateUrlWithFilters();
          return this.#bookingRequestService.getRoomBookings(this.role);
        })
      )
      .subscribe();
  }

  /** Called by (openChange) on every rds-multi-select in the filter form. */
  protected onMultiSelectClose(isOpen: boolean) {
    if (!isOpen) {
      this.#triggerFilter();
    }
  }

  #triggerFilter() {
    const values = this.filterForm.value;
    const snapshot = JSON.stringify(values);
    if (snapshot === this.#appliedFiltersSnapshot) {
      return;
    }
    this.#appliedFiltersSnapshot = snapshot;
    this.#bookingRequestService.setBookingRequestFilters(values);
    this.#bookingRequestService.resetPage();
    this.#updateUrlWithFilters();
    this.#bookingRequestService
      .getRoomBookings(this.role)
      .pipe(take(1))
      .subscribe();
  }
}
